import { createStore } from 'vuex';
import config from '../../config';
import { transform, filter, colorize, deepClone, getTitle } from '../utils/helpers';
import { calculate, fit } from '../utils/calculator';

export default createStore({
	strict: true,
	state: {
		language: 'de',
		i18n: {},
		view: 'configurator',
		shares: [],
		configurator: deepClone(config.configurator),
		resetEnabled: false,
		isResettable: false,
		customNavigation: {
			returnLabel: null,
			continueLabel: null,
			ctaLabel: null,
			ctaTarget: null
		},
		isPublic: false
	},
	getters: {
		statutoryShares(state) {
			const filteredShares = state.shares.filter(
				share => !['free', 'custom'].includes(share.key)
			);

			// Set all shares to their statutory value
			const statutoryShares = transform(filteredShares, item => ({
				...item,
				value: item.statutoryValue
			}));

			return statutoryShares;
		},
		compulsoryShares(state) {
			const filteredShares = state.shares.filter(share => !['custom'].includes(share.key));

			// Set all shares (except the free one) to their minimal value
			const compulsoryShares = transform(filteredShares, item => ({
				...item,
				value: item.key !== 'free' ? item.compulsoryValue : item.maximalValue
			}));

			return compulsoryShares;
		},
		fittedShares(state) {
			const fittedShares = state.shares.filter(share => !['government'].includes(share.key));

			return fittedShares;
		},
		configuratorItems(state) {
			const firstParentelaDeselected =
				state.configurator.find(item => item.key === 'firstParentela').isChecked === false;
			const secondParentelaDeselected =
				state.configurator.find(item => item.key === 'secondParentela').isChecked === false;

			// Select visible items
			const items = state.configurator.filter(item => {
				if (['spouse', 'firstParentela', 'custom'].includes(item.key)) {
					return true;
				}

				if (item.key === 'secondParentela' && firstParentelaDeselected) {
					return true;
				}

				if (item.key === 'thirdParentela' && secondParentelaDeselected) {
					return true;
				}
			});

			// Add label and optional title
			const extendedItems = transform(items, (item, { level, index, groupKey }) => {
				item.isCustom = groupKey === 'custom';

				if (level === 0) {
					item.title = state.i18n[`${item.key}Present`];
					item.hint = state.i18n[`${item.key}PresentHint`];
					item.hintExtended = state.i18n[`${item.key}PresentHintExtended`];
				} else {
					const { mainTitle } = getTitle({
						i18n: state.i18n,
						key: item.key,
						groupKey,
						level,
						index
					});

					item.label = `${state.i18n.name} ${mainTitle}`.trim();

					if (item.isPlaceholder) {
						item.title = mainTitle;
					}
				}

				return item;
			});

			return extendedItems;
		}
	},
	mutations: {
		reset(state) {
			state.view = 'configurator';
			state.shares = [];
			state.configurator = deepClone(config.configurator);

			this.commit('calculateShares');
			this.commit('setIsResettable', false);
		},
		setIsResettable(state, payload) {
			state.isResettable = payload;
		},
		setResetEnabled(state, payload) {
			state.resetEnabled = payload;
		},
		setCustomNavigation(state, payload) {
			state.customNavigation = {
				...state.customNavigation,
				...payload
			};
		},
		initialiseStore(state, { initialData }) {
			if (initialData) {
				const { shares, configurator } = initialData;

				if (configurator) {
					this.commit('setConfigurator', configurator);
				}

				if (shares) {
					this.commit('setShares', calculate(shares));

					if (state.view !== config.VIEWS.SUMMARY) {
						this.commit('showCustomizer');
					}
				}

				this.commit('setIsResettable', true);
			} else if (!state.isResettable) {
				this.commit('calculateShares');
			}
		},
		setLocale(state, { locale }) {
			const language = locale || state.language;
			const i18n = Object.entries(config.i18n).reduce((acc, [key, locales]) => {
				acc[key] = locales[language];

				return acc;
			}, {});

			this.commit('setI18n', i18n);
			this.commit('setLanguage', language);
		},
		setLanguage(state, payload) {
			state.language = payload;
		},
		setI18n(state, payload) {
			state.i18n = payload;
		},
		setView(state, payload) {
			state.view = payload;
		},
		setShares(state, payload) {
			state.shares = payload;
		},
		setConfigurator(state, payload) {
			state.configurator = state.configurator.map(item => {
				const newItem = payload.find(newItem => newItem.key === item.key);

				return newItem || item;
			});
		},
		showConfigurator() {
			this.commit('setView', config.VIEWS.CONFIGURATOR);
		},
		showCustomizer() {
			this.commit('setView', config.VIEWS.CUSTOMIZER);
		},
		showSummary() {
			this.commit('setView', config.VIEWS.SUMMARY);
		},
		calculateShares(state, payload) {
			const calculatedShares = payload || calculate([], { rounded: true });
			const shares = transform(
				calculatedShares,
				(item, { level, index, groupKey, total }) => {
					const isFree = item.key === 'free';
					const isCustom = [item.key, groupKey].includes('custom');
					const isSingle = total === 1 && !!item.parts;

					// Colorize
					item.color = colorize(groupKey, { level, index, hasParts: !!item.parts });

					// Add title
					const { mainTitle, leveledTitle } = getTitle({
						defaultTitle: item.title,
						i18n: state.i18n,
						key: item.key,
						groupKey,
						level,
						index,
						isSingle
					});

					item.title = mainTitle;
					item.leveledTitle = leveledTitle;

					// Whether this share can have an absolute value
					item.isSwitchableAbsolute = isCustom && !item.parts && item.isLegatee;

					// Whether we show compulsory / statutory indicators
					item.isIndicated = !isFree && !isCustom;

					// Show as deceased unless it is a placeholder
					item.isDeceased = item.parts && level > 0 && !item.isPlaceholder;

					// Set default value
					if (item.value === undefined) {
						item.value = isFree ? item.maximalValue : item.compulsoryValue;
					}

					return item;
				}
			);

			state.shares = shares;
		},
		setShareValue(state, payload) {
			const { key, targetValue } = payload;
			const updatedShares = transform(state.shares, item => {
				if (item.key === key) {
					item.targetValue = targetValue;
				}

				return item;
			});
			const shares = fit(updatedShares, { rounded: true });

			state.shares = shares;
		},
		setShareFixed(state, payload) {
			const { key, isFixed } = payload;
			const shares = transform(state.shares, item => {
				if (item.key === key) {
					item.isFixed = isFixed;
				}

				return item;
			});

			state.shares = shares;
		},
		setShareAbsolute(state, payload) {
			const { key, isAbsolute } = payload;
			const updatedShares = transform(state.shares, item => {
				if (item.key === key) {
					item.isAbsolute = isAbsolute;

					if (isAbsolute) {
						item.targetValue = 0;
					} else {
						delete item.absoluteValue;
					}
				}

				return item;
			});

			// Run fitting algorithm if item was switched to absolute (we need to remove its previous value)
			const shares = isAbsolute ? fit(updatedShares, { rounded: true }) : updatedShares;

			state.shares = shares;
		},
		setShareAbsoluteValue(state, payload) {
			const { key, targetValue } = payload;
			const shares = transform(state.shares, item => {
				if (item.key === key) {
					item.absoluteValue = targetValue;
				}

				return item;
			});

			state.shares = shares;
		},
		updateSharesFromConfigurator(state) {
			// Remove deceased parent shares without descendants
			const configuredShares = filter(state.configurator, (item, { level }) => {
				if (level > 2) {
					return true;
				}

				const isDeceased = item.parts && !item.parts.find(part => !part.isDeceased);
				const isValidFirstLevel = level === 0 && item.isChecked && !isDeceased;
				const isValidNestedLevel = level > 0 && !(item.parts && item.parts.length === 0);
				const isValid = isValidFirstLevel || isValidNestedLevel;

				return isValid;
			});

			// Calculate
			const calculatedShares = calculate(configuredShares, { rounded: true });

			// Re-add deceased parent shares without descendants
			const shares = transform(calculatedShares, (share, { level, index, groupKey }) => {
				if (level > 1) {
					return share;
				}

				// Find corresponding configurator item
				let configuratorItem = state.configurator.find(item =>
					[share.key, groupKey].includes(item.key)
				);

				if (level === 1) {
					configuratorItem = configuratorItem.parts[index];
				}

				// Add missing configurator item parts to share
				if (
					configuratorItem &&
					(configuratorItem.parts?.length || 0) > (share.parts?.length || 0)
				) {
					const allDeceased = !configuratorItem.parts.find(part => !part.parts);

					if (!allDeceased) {
						share.parts = configuratorItem.parts.map(configuratorPart => {
							const sharePart = share.parts.find(
								sharePart => sharePart.key === configuratorPart.key
							) || { compulsoryValue: 0, statutoryValue: 0 };

							return { ...configuratorPart, ...sharePart };
						});
					}
				}

				return share;
			});

			this.commit('calculateShares', shares);
			this.commit('setIsResettable', true);
		},
		toggleConfiguratorItem(state, payload) {
			const { key, isChecked } = payload;

			// Update target item state
			let configurator = state.configurator.map(item => {
				if (item.key === key) {
					item.isChecked = isChecked;
				}

				return item;
			});

			// Reset target and dependent items
			const resetSecondParentela =
				state.configurator.find(item => item.key === 'firstParentela').isChecked !== false;
			const resetThirdParentela =
				state.configurator.find(item => item.key === 'secondParentela').isChecked !== false;

			configurator = configurator.map(item => {
				const isDependentToReset =
					(item.key === 'secondParentela' && resetSecondParentela) ||
					(item.key === 'thirdParentela' &&
						(resetSecondParentela || resetThirdParentela));

				if (!item.isChecked || isDependentToReset) {
					const defaultItem = config.configurator.find(
						candidate => candidate.key === item.key
					);

					// When resetting a dependant, the checked state is set to `undefined`
					// Other items can keep a value of `false`
					const isChecked = isDependentToReset ? undefined : item.isChecked;

					return {
						...defaultItem,
						isChecked
					};
				}

				return item;
			});

			state.configurator = configurator;

			this.commit('updateSharesFromConfigurator');
		},
		setConfiguratorItemTitle(state, payload) {
			const { key, title } = payload;
			const configurator = transform(state.configurator, item => {
				if (item.key === key) {
					item.title = title;
				}

				return item;
			});

			state.configurator = configurator;

			this.commit('updateSharesFromConfigurator');
		},
		setConfiguratorItemDeceased(state, payload) {
			const { key, isDeceased } = payload;

			// Add descendant to deceased item unless it is a parent/grandparent
			let configurator = transform(state.configurator, (item, { level, groupKey }) => {
				if (item.key === key) {
					if (isDeceased) {
						const isParent = groupKey === 'secondParentela' && level === 1;
						const isGrandParent = groupKey === 'thirdParentela' && level === 2;

						if (isParent || isGrandParent) {
							item.parts = [];
						} else {
							item.parts = [{ key: `${item.key}-item1` }];
						}
					} else {
						delete item.parts;
					}
				}

				return item;
			});

			// Combine descendants of deceased parents/grandparents
			configurator = transform(configurator, (item, { level, groupKey }) => {
				const isParentGroup = groupKey === 'secondParentela' && level === 0;
				const isGrandParentGroup = groupKey === 'thirdParentela' && level === 1;
				const isDeceased = item.parts && !item.parts.find(part => !part.parts);

				if (isParentGroup || isGrandParentGroup) {
					item.parts = item.parts
						.map((part, index) => {
							if (isDeceased && index === 1) {
								const siblings = item.parts.reduce((acc, sibling) => {
									acc = acc.concat(sibling.parts);

									return acc;
								}, []);

								// Deceased parents require at least one descendant
								part.parts =
									siblings.length || isGrandParentGroup
										? siblings
										: [{ key: `${item.key}-item1` }];
							}

							return part;
						})
						.map((part, index) => {
							if (isDeceased && index === 0) {
								part.parts = [];

								part.hideParts = true;
							} else {
								delete part.hideParts;
							}

							return part;
						});
				}

				return item;
			});

			state.configurator = configurator;

			this.commit('updateSharesFromConfigurator');
		},
		setConfiguratorItemType(state, payload) {
			const { key, isLegatee } = payload;
			const configurator = transform(state.configurator, item => {
				if (item.key === key) {
					item.isLegatee = isLegatee;
					item.isAbsolute = isLegatee;
				}

				return item;
			});

			state.configurator = configurator;

			this.commit('updateSharesFromConfigurator');
		},
		addConfiguratorItemPart(state, payload) {
			const { key } = payload;
			const configurator = transform(state.configurator, item => {
				if (item.key === key) {
					const part = {
						key: `${item.key}-item${item.parts.length + 1}`,
						isRemovable: true
					};

					if (key === 'custom') {
						part.isLegatee = true;
						part.isAbsolute = true;
					}

					item.parts.push(part);
				}

				return item;
			});

			state.configurator = configurator;

			this.commit('updateSharesFromConfigurator');
		},
		removeConfiguratorItemPart(state, payload) {
			const { key } = payload;
			const configurator = transform(state.configurator, item => {
				if (item.parts) {
					item.parts = item.parts.filter(part => part.key !== key);
				}

				return item;
			});

			state.configurator = configurator;

			this.commit('updateSharesFromConfigurator');
		},
		setIsPublic(state, payload) {
			state.isPublic = payload;
		}
	}
});
